import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useIntl } from '@/intl'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { getRoomExtraInfo, getRoomParticipantDiff, isRoomChanged } from '../selectors'
import styles from './RoomExtraInfoSheetPortal.module.css'
import {
  FieldSection,
  FieldSectionTitle,
  SheetContentStyled,
  SheetHeaderStyled,
} from './SheetPortal'

export function RoomExtraInfoSheetPortal() {
  const roomExtraInfo = useGroupResponsibleStore(getRoomExtraInfo)
  const activeExtraInfoId = useGroupResponsibleStore(state => state.activeExtraInfoId)
  const changeExtraInfoId = useGroupResponsibleStore(state => state.changeExtraInfoId)
  const openRoomAssignment = useGroupResponsibleStore(state => state.openRoomAssignment)
  const activateRoomAssignment = useFeatureFlag('RIO_ENABLE_GROUP_RESPONSIBLE_ROOM_ASSIGNMENT')
  const isBlocked = useGroupResponsibleStore(state => state.bookingData.group.isBlocked)
  const isChanged = useGroupResponsibleStore(state => isRoomChanged(state, activeExtraInfoId))
  const diff = useGroupResponsibleStore(state => getRoomParticipantDiff(state, activeExtraInfoId))
  const intl = useIntl()

  const isOpen = !!activeExtraInfoId

  function handleOpenChange(open: boolean) {
    if (!open) {
      changeExtraInfoId('')
    }
  }

  function renderHosts() {
    if (isChanged) {
      if (diff.unchanged.length === 0 && diff.added.length === 0 && diff.removed.length === 0) {
        return <Typography>-</Typography>
      }

      return (
        <>
          {diff.unchanged.map(participant => (
            <Typography as="div" key={participant.id}>
              {participant.name}
            </Typography>
          ))}
          {diff.added.map(participant => (
            <Typography as="div" key={participant.id} className={styles.addedParticipant}>
              {participant.name}
            </Typography>
          ))}
          {diff.removed.map(participant => (
            <Typography as="div" key={participant.id} className={styles.removedParticipant}>
              {participant.name}
            </Typography>
          ))}
        </>
      )
    }

    if (roomExtraInfo.participants.length > 0) {
      return roomExtraInfo.participants.map(participant => (
        <Typography as="div" key={participant}>
          {participant}
        </Typography>
      ))
    }

    return <Typography>-</Typography>
  }

  return (
    <Sheet.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Sheet.Body side="right">
        <SheetHeaderStyled
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'groups_responsible_room' })}
        />
        <SheetContentStyled>
          <div className={styles.titleWrapper}>
            <Typography as="div" variant="display-g-regular">
              {roomExtraInfo.name}
            </Typography>
            {isChanged && (
              <Tag variant="informative-tinted" size="M">
                {intl.formatMessage({ id: 'groups_responsible_room_changed_label' })}
              </Tag>
            )}
          </div>
          {roomExtraInfo.needsAction && (
            <Banner className={styles.warningBanner} hasIcon size="M" color="warning">
              <Banner.TextsWrapper>
                <Banner.Text>
                  {intl.formatMessage({ id: 'groups_responsible_action_required_message' })}
                </Banner.Text>
              </Banner.TextsWrapper>
            </Banner>
          )}
          <FieldSection>
            <div>
              <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
                {intl.formatMessage({ id: 'groups_responsible_hosts' })}
              </FieldSectionTitle>
              {renderHosts()}
            </div>
          </FieldSection>
          {activateRoomAssignment && (
            <>
              <Button
                className={cn(styles.editButton, isBlocked && styles.editButtonBlocked)}
                fullWidth
                onClick={() => openRoomAssignment(activeExtraInfoId)}
                disabled={isBlocked}
              >
                {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_edit_button' })}
              </Button>
              {isBlocked && (
                <Banner className={styles.blockedBanner} hasIcon size="M" color="informative">
                  <Banner.TextsWrapper>
                    <Banner.Text>
                      {intl.formatMessage({ id: 'groups_responsible_room_assignment_still_saving' })}
                    </Banner.Text>
                  </Banner.TextsWrapper>
                </Banner>
              )}
            </>
          )}
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_room_type' })}
            </FieldSectionTitle>
            <Typography>{roomExtraInfo.type}</Typography>
          </FieldSection>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_accommodation' })}
            </FieldSectionTitle>
            <Typography>{roomExtraInfo.accommodationName}</Typography>
          </FieldSection>
          {roomExtraInfo.pinCode && (
            <FieldSection>
              <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
                {intl.formatMessage({ id: 'groups_responsible_pin_code' })}
              </FieldSectionTitle>
              <Typography>{roomExtraInfo.pinCode}</Typography>
            </FieldSection>
          )}
        </SheetContentStyled>
      </Sheet.Body>
    </Sheet.Root>
  )
}
